import React, { useRef, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import "../styles/ContactSection.css";

/* ─────────────────────────────────────────────
   Contact details — same numbers used across
   the site (Navbar, WhatsApp booking flows, etc.)
───────────────────────────────────────────── */
const PHONE_NUMBER = "7358798182";
const WHATSAPP_NUMBER = "917358798182";
const EMAIL_ADDRESS = "tanbiomedicalab@gmail.com";
const EMAIL_SUBJECT = "Enquiry - TANBIO Biomed Genomics Laboratory";

/* Detects actual mobile/tablet devices (not just a narrow browser
   window) so email can route to the right flow — plain mailto:
   works reliably on-device, but frequently fails silently on
   desktop browsers with no configured mail client. */
function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|IEMobile|Opera Mini|BlackBerry|webOS/i.test(
    navigator.userAgent
  );
}

function openWhatsApp(message) {
  const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    message
  )}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

function openCall() {
  window.location.href = `tel:${PHONE_NUMBER}`;
}

function openEmail() {
  if (isMobileDevice()) {
    // Native mail app handles mailto: reliably on phones/tablets
    window.location.href = `mailto:${EMAIL_ADDRESS}?subject=${encodeURIComponent(
      EMAIL_SUBJECT
    )}`;
  } else {
    // Most desktop browsers have no mail client configured, so
    // mailto: silently does nothing — open Gmail compose instead
    const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${EMAIL_ADDRESS}&su=${encodeURIComponent(
      EMAIL_SUBJECT
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

const contactCards = [
  {
    icon: "fa-solid fa-phone",
    bgColor: "cs-icon-blue",
    title: "Call Us",
    primary: "73587 98182",
    secondary: "We are just a call away",
    action: "call",
  },
  {
    icon: "fa-brands fa-whatsapp",
    bgColor: "cs-icon-green",
    title: "WhatsApp",
    primary: "73587 98182",
    secondary: "Chat with us on WhatsApp",
    action: "whatsapp",
    whatsappMessage:
      "Hi, I'd like to get in touch with TANBIO Biomed Genomics Laboratory.",
  },
  {
    icon: "fa-solid fa-envelope",
    bgColor: "cs-icon-blue",
    title: "Email Us",
    primary: "tanbiomedicalab@gmail.com",
    secondary: "We reply within 24 hours",
    action: "email",
  },
  {
    icon: "fa-solid fa-file-lines",
    bgColor: "cs-icon-blue",
    title: "Report Delivery",
    primary: "Reports via Email / WhatsApp",
    secondary: "Printed reports available",
    action: "whatsapp",
    whatsappMessage:
      "Hi, I'd like an update on my report delivery status.",
  },
];

// Exact coordinates: 10°43'50.7"N 79°37'52.0"E → 10.730744, 79.631105
const MAP_EMBED =
  "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d979.25!2d79.631105!3d10.730744!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTDCsDQzJzUwLjciTiA3OcKwMzcnNTIuMCJF!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin";

export default function ContactSection() {
  function handleCardClick(card) {
    switch (card.action) {
      case "call":
        openCall();
        break;
      case "whatsapp":
        openWhatsApp(card.whatsappMessage);
        break;
      case "email":
        openEmail();
        break;
      default:
        break;
    }
  }

  function handleCardKeyDown(e, card) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardClick(card);
    }
  }

  return (
    <section className="cs-section">
      <Container fluid="xxl">
        {/* ── 4 Contact Cards ── */}
        <div className="cs-cards-row">
          {contactCards.map((card, i) => (
            <div
              className="cs-card"
              key={i}
              role="button"
              tabIndex={0}
              onClick={() => handleCardClick(card)}
              onKeyDown={(e) => handleCardKeyDown(e, card)}
              aria-label={`${card.title}: ${card.primary}`}
            >
              <div className={`cs-icon-wrap ${card.bgColor}`}>
                <i className={card.icon}></i>
              </div>
              <div className="cs-card-body">
                <p className="cs-card-title">{card.title}</p>
                <p className="cs-card-primary">{card.primary}</p>
                <p className="cs-card-secondary">{card.secondary}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Map Block ── */}
        <div className="cs-map-block">
          <div className="cs-map-heading">
            <i className="fa-solid fa-location-dot cs-pin-icon"></i>
            <h2 className="cs-map-title">Find Us Here</h2>
          </div>

          {/* padding-bottom aspect ratio — fills full width, no overflow */}
          <div className="cs-map-area">
            <iframe
              title="TANBIO Lab Location"
              src={MAP_EMBED}
              className="cs-iframe"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </Container>
    </section>
  );
}